import {useMemo} from 'react'

import {OptimizerScheduleStatus} from '../../../../shared/enums'
import {useCurrentScheduleQuery} from '../../../../shared/hooks/api/schedules/useCurrentScheduleQuery'
import {useOptimizedSchedulesAndRuns} from '../../../../shared/hooks/api/schedules/useOptimizedSchedulesQuery'
import {OptimizerRun, OptimizedSchedule, Schedule} from '../../../../shared/interfaces/api'
import {usePlanningChartStartEnd} from '../../../page-planning/dailyPlanning/PlanningChartStartEndProvider'
import {OptimizerScheduleType} from '../../shared/enums/optimizerSchedules'

export type OptimizerSchedule =
  | ({type: OptimizerScheduleType.Manual} & Schedule)
  | ({type: OptimizerScheduleType.Optimized; isLatestAcceptedSchedule: boolean} & OptimizedSchedule)
  | ({type: OptimizerScheduleType.Calculating} & OptimizerRun)

export function useOptimizerPlanOverview() {
  const {startOfPlan, endOfPlan} = usePlanningChartStartEnd()

  const {data: manualSchedule} = useCurrentScheduleQuery({range: [startOfPlan, endOfPlan]})
  const {data: optimizedSchedulesAndRuns} = useOptimizedSchedulesAndRuns()

  if (!manualSchedule || !optimizedSchedulesAndRuns)
    throw new Error('Manual schedule and optimized schedules and runs must be loaded beforehand')

  return useMemo<OptimizerSchedule[]>(() => {
    const currentManualSchedule = {...manualSchedule, type: OptimizerScheduleType.Manual}

    const latestAcceptedSchedule = optimizedSchedulesAndRuns.optimizedSchedules
      .filter((schedule) => schedule.status === OptimizerScheduleStatus.Accepted)
      .sort(
        (a, b) => new Date(a.lastSaved.savedAt).getTime() - new Date(b.lastSaved.savedAt).getTime()
      )
      .pop()

    const optimizedSchedules = optimizedSchedulesAndRuns.optimizedSchedules
      .map((schedule) => ({
        ...schedule,
        isLatestAcceptedSchedule: schedule.scheduleId === latestAcceptedSchedule?.scheduleId,
        type: OptimizerScheduleType.Optimized
      }))
      .sort(
        (a, b) =>
          new Date(a.optimizationDetails.triggeredOn).getTime() -
          new Date(b.optimizationDetails.triggeredOn).getTime()
      )

    const optimizedRuns = optimizedSchedulesAndRuns.optimizerRuns.map((run) => ({
      ...run,
      type: OptimizerScheduleType.Calculating
    }))

    const optimizerSchedules = [
      currentManualSchedule,
      ...optimizedSchedules,
      ...optimizedRuns
    ].filter(Boolean) as OptimizerSchedule[]

    return optimizerSchedules
  }, [
    manualSchedule,
    optimizedSchedulesAndRuns?.optimizedSchedules,
    optimizedSchedulesAndRuns?.optimizerRuns
  ])
}
